import "./site/error-beacon";
import { Resonance, ResonanceTap, ResonanceKind } from "./games/resonance/resonance";
import { recordRun, showXpToast } from "./site/profile";
import { getDailySeed, markDailyComplete, isDailyRun } from "./site/daily";
import "./ui/styles.css";

const PB_KEY = "aether.resonance_pb";
const HANDLE_KEY = "aether.handle";

interface StartResp { run_id: string; seed: number; token: string; game: string }

async function startResonanceRun(): Promise<StartResp | null> {
  try {
    const res = await fetch("/api/runs/resonance", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StartResp;
  } catch { return null; }
}

interface FinishExtras {
  /** v5(d): client HUD's final score estimate — server records the
   *  drift so we can alert when it exceeds 10% (parity break). */
  score_client_hud: number;
  /** v5(d): ms from page load to first tap. Onboarding signal —
   *  see dossier 12-resonance/07-telemetry.md §3 Q10. */
  time_to_first_tap_ms: number | null;
}

async function finishResonanceRun(
  runId: string,
  handle: string,
  taps: ResonanceTap[],
  kind: ResonanceKind,
  extras: FinishExtras,
) {
  try {
    const res = await fetch(`/api/runs/resonance/${runId}`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ handle, taps, kind, ...extras }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

/** v5(d): render the run's arc — peak vs final — as a one-line tag
 *  that characterizes the shape of the run. peak / final come from
 *  the server response (final_controlled is computed at the last
 *  tick and crosses the wire post-submit). See dossier
 *  12-resonance/07-telemetry.md §6 #4. */
function arcTag(peak: number, final: number): string {
  if (peak === 0) return "";
  if (peak < 5) return "early exit · peak < 5 held";
  const ratio = final / peak;
  if (ratio >= 0.85) return `held strong · ${final} of peak ${peak}`;
  if (ratio >= 0.55) return `softened late · peak ${peak} → ${final}`;
  if (ratio >= 0.25) return `fell off late · peak ${peak} → ${final}`;
  return `late collapse · peak ${peak} → ${final}`;
}

/** v5(a) shipped opt-in hex via `?hex=1`. v5.1 flips the default: hex
 *  is the live topology; `?square=1` falls back to the legacy
 *  4-neighbor toroidal grid. Leaderboard rows are scoped by `kind`. */
function readKindFromUrl(): ResonanceKind {
  try {
    const params = new URL(window.location.href).searchParams;
    if (params.get("square") === "1" || params.get("square") === "true") return "square";
    return "hex";
  } catch {
    return "hex";
  }
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudHeld = document.getElementById("hud-held")!;
const hudCluster = document.getElementById("hud-cluster")!;
const hudScore = document.getElementById("hud-score")!;
const hudAi = document.getElementById("hud-ai");
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;

let game: Resonance | null = null;
let activeRunId: string | null = null;
let pendingTaps: { taps: ResonanceTap[]; kind: ResonanceKind; extras: FinishExtras } | null = null;

async function startRun() {
  if (game) game.dispose();
  const dseed = getDailySeed();
  const runStart = dseed === null ? await startResonanceRun() : null;
  activeRunId = runStart?.run_id ?? null;
  const seed = dseed ?? runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);

  const kind = readKindFromUrl();
  game = new Resonance(canvas, {
    onHud: (held, cluster, scoreEstimate, aiControlled) => {
      hudHeld.textContent = String(held);
      hudCluster.textContent = String(cluster);
      hudScore.textContent = scoreEstimate.toLocaleString();
      if (hudAi) {
        hudAi.textContent = String(aiControlled);
        hudAi.classList.toggle("danger", aiControlled >= held && aiControlled > 0);
      }
    },
    onGameOver: async ({ taps, scoreEstimate, peakControlled, peakCluster, aiPeakControlled, kind: playedKind, firstTapMs }) => {
      hud.dataset.state = "over";
      msgTitle.textContent = "Run complete";
      const aiTag = aiPeakControlled > 0 ? ` · rival peak ${aiPeakControlled}` : "";
      msgBody.textContent = `${scoreEstimate.toLocaleString()} pts · peak ${peakControlled} held · biggest cluster ${peakCluster}${aiTag}`;
      btnStart.textContent = "Try again";
      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        if (scoreEstimate > pb) localStorage.setItem(PB_KEY, String(scoreEstimate));
      } catch { /* private */ }
      const xp = recordRun({ game: "resonance", score: scoreEstimate });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);
      if (isDailyRun()) markDailyComplete("resonance", scoreEstimate);

      if (!activeRunId) return;
      // v5(d): client telemetry per dossier §3. firstTapMs comes from
      // the Resonance class which already tracks its own startedAt and
      // computes the page-load offset internally.
      const extras: FinishExtras = {
        score_client_hud: scoreEstimate,
        time_to_first_tap_ms: firstTapMs,
      };
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishResonanceRun(activeRunId, handle, taps, playedKind, extras);
        if (res) {
          msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
          const tag = arcTag(res.peak_controlled ?? peakControlled, res.final_controlled ?? 0);
          if (tag) msgBody.textContent += ` · ${tag}`;
        }
      } else {
        pendingTaps = { taps, kind: playedKind, extras };
        submitForm.hidden = false;
      }
    },
  }, seed, kind);
  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = kind === "square" ? "Resonance · square (legacy)" : "Resonance";
  msgBody.textContent = "";
}

btnStart.addEventListener("click", startRun);

submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!activeRunId || !pendingTaps) return;
  const value = handleInputOverlay.value.trim();
  if (!/^[A-Za-z0-9_-]{1,16}$/.test(value)) return;
  localStorage.setItem(HANDLE_KEY, value);
  const res = await finishResonanceRun(activeRunId, value, pendingTaps.taps, pendingTaps.kind, pendingTaps.extras);
  pendingTaps = null;
  submitForm.hidden = true;
  if (res) {
    msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
    const tag = arcTag(res.peak_controlled ?? 0, res.final_controlled ?? 0);
    if (tag) msgBody.textContent += ` · ${tag}`;
  }
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") startRun();
});
